import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Battery, ChevronRight, Zap, Shield, Sparkles, Leaf, Phone,
  ClipboardList, CheckCircle, Wrench, Layers, AlertTriangle, Boxes, Cpu, Gauge,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import SEO from '../components/SEO';
import {
  GOODWE_INVERTERS, GOODWE_PACKAGES_512, GOODWE_PACKAGES_832,
  startingPrice, fmtAUD, GOODWE_MODULE_INFO,
} from '../data/goodwePackages';

const RANGE_IMG = '/product-images/goodwe/esa-system-5.webp';
const SYSTEM_IMG = '/product-images/goodwe/esa-built-2.webp';

const FAQS = [
  { q: 'Can I mix 5.12 kWh and 8.32 kWh G20 batteries in one stack?', a: 'No. The same stack should use the same GoodWe G20 battery module type only — either all 5.12 kWh modules or all 8.32 kWh modules.' },
  { q: 'Why are you using G20 instead of G21?', a: 'G20 is our preferred GoodWe battery series for standard packages. All of our GoodWe ESA packages are built around G20 modules only.' },
  { q: 'Do I need a single-phase or three-phase inverter?', a: "This depends on the property's electrical supply and overall system requirements. Our team will confirm the right option for your home." },
  { q: 'Is the price final?', a: 'The advertised price is based on standard installation conditions. Additional costs may apply depending on site requirements, confirmed after the site inspection.' },
  { q: 'What does the site inspection check?', a: 'The switchboard, battery location, cable run, wall surface, base/slab suitability, and any additional installation requirements.' },
  { q: 'What happens if additional costs are required?', a: 'They will be confirmed after the site inspection and before installation is organised.' },
  { q: 'Is the BSTC rebate included in the price?', a: 'The shown out-of-pocket price assumes the customer assigns the applicable BSTC rebate to Supa Energy Hub.' },
];

const PackageGrid = ({ packages, moduleType, testid }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={testid}>
    {packages.map((p) => (
      <Link
        key={p.slug}
        to={`/${p.slug}`}
        className="group bg-white rounded-2xl border-2 border-[#B5D43B]/20 hover:border-[#B5D43B] hover:shadow-xl transition-all overflow-hidden flex flex-col"
        data-testid={`goodwe-card-${p.slug}`}
      >
        <div className="bg-gradient-to-br from-[#3D5A47] to-[#1a2820] p-5 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #B5D43B 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <Battery className="w-4 h-4 text-[#B5D43B]" />
              <span className="text-[10px] uppercase tracking-widest text-[#B5D43B] font-bold">GoodWe G20 · {moduleType} kWh</span>
            </div>
            <p className="text-4xl font-black leading-none">{p.kwh.toFixed(2)}<span className="text-lg text-white/60"> kWh</span></p>
            <p className="text-xs text-white/70 mt-2">{p.modules} × {GOODWE_MODULE_INFO[moduleType].label} module{p.modules > 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="p-5 flex flex-col flex-1">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Starting from</p>
          <p className="text-3xl font-black text-[#3D5A47] leading-tight mt-0.5">{fmtAUD(startingPrice(p))}</p>
          <p className="text-[11px] text-gray-500 mt-1">installed after estimated BSTC discount*</p>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs">
            <span className="inline-flex items-center gap-1 text-gray-600"><Zap className="w-3 h-3" /> {Object.values(p.prices).filter((v) => v != null).length} inverter options</span>
            <span className="inline-flex items-center gap-1 text-[#3D5A47] font-semibold group-hover:text-[#B5D43B]">View <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
          </div>
        </div>
      </Link>
    ))}
  </div>
);

const GoodWePackagesPage = () => (
  <div className="min-h-screen">
    <SEO
      title="GoodWe ESA G20 Battery Packages — Fully Installed"
      description="GoodWe ESA G20 home battery packages from 5.12 kWh to 49.92 kWh — fully supplied and installed by Supa Energy Hub. Single & three-phase GoodWe ESA hybrid inverter options, transparent out-of-pocket pricing after estimated BSTC discount."
      jsonLd={[
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        },
      ]}
    />
    <Header />
    <main>
      {/* HERO */}
      <section className="pt-28 pb-12 sm:pb-16 relative overflow-hidden bg-gradient-to-br from-[#f6f8f3] via-white to-[#eaf2dc]">
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #3D5A47 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="absolute top-20 right-0 w-72 h-72 bg-[#B5D43B]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" data-testid="goodwe-main-breadcrumb">
            <Link to="/" className="hover:text-[#3D5A47]">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/packages" className="hover:text-[#3D5A47]">Packages</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-[#3D5A47] font-medium">GoodWe Battery Packages</span>
          </nav>
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 bg-[#B5D43B]/15 border border-[#B5D43B]/40 rounded-full px-3 py-1 mb-4">
                <Sparkles className="w-3.5 h-3.5 text-[#3D5A47]" />
                <span className="text-[11px] font-bold tracking-wider uppercase text-[#3D5A47]">GoodWe ESA · Fully Supplied + Installed</span>
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#3D5A47] leading-[1.1] tracking-tight">
                GoodWe ESA <span className="text-[#B5D43B] bg-[#3D5A47] px-2 rounded-md">G20</span> Battery Packages
              </h1>
              <p className="text-base sm:text-lg text-gray-700 mt-5 max-w-2xl leading-relaxed">
                Store your solar and power your home through the evening with the GoodWe ESA all-in-one battery system.
                Choose a package from <strong>5.12 kWh up to 49.92 kWh</strong> — fully supplied, installed and rebate-handled,
                at one transparent out-of-pocket price.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a href="#packages">
                  <Button className="bg-[#3D5A47] hover:bg-[#2a4032] text-white gap-2 text-sm sm:text-base font-semibold px-5 py-5 rounded-xl shadow-lg shadow-[#3D5A47]/20" data-testid="goodwe-main-cta">
                    Compare GoodWe Battery Packages <ArrowRight className="w-4 h-4" />
                  </Button>
                </a>
                <Link to="/get-estimate?source=goodwe-main">
                  <Button variant="outline" className="bg-white border-[#3D5A47]/20 text-[#3D5A47] hover:bg-[#f8faf5] gap-2 text-sm sm:text-base px-5 py-5 rounded-xl">
                    <ClipboardList className="w-4 h-4" /> Get My GoodWe Quote
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-gradient-to-br from-[#1a2820] to-[#3D5A47] rounded-2xl shadow-xl overflow-hidden">
                <img src={RANGE_IMG} alt="GoodWe ESA G20 battery system range — scalable from 5.12 kWh to 49.92 kWh" className="w-full h-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT GOODWE */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">The brand</span>
            <h2 className="heading-2 text-[#3D5A47] mb-4">Who is GoodWe?</h2>
            <p className="text-gray-700 text-sm leading-relaxed mb-4">
              GoodWe is one of the world&rsquo;s largest hybrid inverter and battery storage manufacturers, with millions of
              installations worldwide. Their residential energy storage systems are known for reliability, safety and smart
              energy management — letting your home use more of its own solar, day and night.
            </p>
            <p className="text-gray-700 text-sm leading-relaxed">
              The <strong>GoodWe ESA</strong> range is an all-in-one residential storage solution that combines a hybrid
              inverter, battery and energy management in one neat, expandable system — backed by GoodWe&rsquo;s battery warranty.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { icon: Cpu, t: 'All-in-one ESA system', d: 'Hybrid inverter + battery + energy management combined.' },
              { icon: Leaf, t: 'Safe LFP chemistry', d: 'GoodWe G20 modules use LiFePO4 — stable and long-lasting.' },
              { icon: Boxes, t: 'Stackable & expandable', d: 'Add G20 modules to grow your storage over time.' },
              { icon: Shield, t: 'Whole-home backup ready', d: 'Keep essential circuits running during an outage.' },
            ].map((f, i) => (
              <div key={i} className="bg-[#f8faf5] rounded-xl p-5 border border-gray-100">
                <div className="w-10 h-10 rounded-lg bg-[#B5D43B]/15 flex items-center justify-center mb-3"><f.icon className="w-5 h-5 text-[#3D5A47]" /></div>
                <h3 className="font-bold text-sm text-[#3D5A47] mb-1">{f.t}</h3>
                <p className="text-xs text-gray-600">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW STACKING WORKS + MODULES */}
      <section className="py-16 bg-[#f8faf5] border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">GoodWe G20 batteries</span>
          <h2 className="heading-2 text-[#3D5A47] mb-3">How the stackable battery system works</h2>
          <p className="text-gray-700 text-sm max-w-3xl leading-relaxed mb-8">
            The GoodWe ESA system is built from stackable <strong>G20 battery modules</strong>. You start with one module and
            stack more on top to reach the capacity your home needs. Each stack uses one module type only — so your storage
            grows in clean, predictable steps.
          </p>

          <div className="grid md:grid-cols-2 gap-5">
            {['5.12', '8.32'].map((mt) => {
              const info = GOODWE_MODULE_INFO[mt];
              return (
                <div key={mt} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm" data-testid={`goodwe-module-${mt}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-xl bg-[#3D5A47] flex items-center justify-center"><Battery className="w-5 h-5 text-[#B5D43B]" /></div>
                    <div>
                      <h3 className="font-bold text-[#3D5A47]">{info.label} Battery Module</h3>
                      <p className="text-xs text-gray-500">{info.usable} · LFP (LiFePO4)</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">{info.bestFor}</p>
                  <p className="text-xs text-gray-500">Up to {info.maxModules} modules per stack in our standard packages.</p>
                </div>
              );
            })}
          </div>

          {/* Difference + rules */}
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            <div className="bg-white rounded-xl p-5 border border-gray-100">
              <div className="flex items-center gap-2 mb-2"><Gauge className="w-5 h-5 text-[#3D5A47]" /><h3 className="font-bold text-sm text-[#3D5A47]">5.12 vs 8.32 kWh</h3></div>
              <p className="text-xs text-gray-600">The 5.12 kWh module lets you fine-tune capacity in smaller steps. The 8.32 kWh module gives more storage per module, so you reach higher capacity with fewer modules.</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-[#B5D43B]/40">
              <div className="flex items-center gap-2 mb-2"><Layers className="w-5 h-5 text-[#3D5A47]" /><h3 className="font-bold text-sm text-[#3D5A47]">Why G20 only</h3></div>
              <p className="text-xs text-gray-600">G20 is our preferred GoodWe battery series for standard packages. We build every GoodWe package around G20 modules — we do not use or promote G21.</p>
            </div>
            <div className="bg-[#fdf4ec] rounded-xl p-5 border border-orange-200">
              <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5 text-orange-600" /><h3 className="font-bold text-sm text-[#3D5A47]">Don&rsquo;t mix module sizes</h3></div>
              <p className="text-xs text-gray-600">Each stack must use the same G20 module type only. Never mix 5.12 kWh and 8.32 kWh modules in the same stack.</p>
            </div>
          </div>
        </div>
      </section>

      {/* INVERTER OPTIONS (NO PRICES) */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Inverter options</span>
          <h2 className="heading-2 text-[#3D5A47] mb-3">GoodWe ESA hybrid inverter range</h2>
          <p className="text-gray-700 text-sm max-w-3xl leading-relaxed mb-6">
            Every package pairs a GoodWe G20 battery with a GoodWe ESA hybrid inverter. Choose a single-phase or three-phase
            inverter to suit your property&rsquo;s electrical supply and solar size. Pricing for each option is shown on the
            individual package pages.
          </p>
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm" data-testid="goodwe-inverter-options-table">
            <table className="w-full text-sm">
              <thead className="bg-[#3D5A47] text-white text-left">
                <tr>
                  <th className="px-4 sm:px-6 py-3 font-semibold">Inverter Option</th>
                  <th className="px-4 sm:px-6 py-3 font-semibold">Phase</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {GOODWE_INVERTERS.map((inv) => (
                  <tr key={inv.id}>
                    <td className="px-4 sm:px-6 py-3.5">
                      <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#3D5A47]" /><span className="font-semibold text-[#3D5A47]">{inv.label}</span></div>
                    </td>
                    <td className="px-4 sm:px-6 py-3.5 text-gray-600">{inv.phase === 3 ? '3 Phase' : '1 Phase'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section id="packages" className="py-16 bg-[#f8faf5] border-y border-gray-100 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Battery package options</span>
          <h2 className="heading-2 text-[#3D5A47] mb-2">GoodWe G20 packages up to 49.92 kWh</h2>
          <p className="text-gray-700 text-sm max-w-3xl mb-8">
            Every price below is your installed, out-of-pocket cost after the estimated BSTC discount. Tap a package to see
            full inverter pricing, inclusions and the site inspection process.
          </p>

          <h3 className="text-lg font-bold text-[#3D5A47] mb-4 flex items-center gap-2">
            <Battery className="w-5 h-5 text-[#B5D43B]" /> G20 5.12 kWh module packages
          </h3>
          <PackageGrid packages={GOODWE_PACKAGES_512} moduleType="5.12" testid="goodwe-grid-512" />

          <h3 className="text-lg font-bold text-[#3D5A47] mb-4 mt-12 flex items-center gap-2">
            <Battery className="w-5 h-5 text-[#B5D43B]" /> G20 8.32 kWh module packages
          </h3>
          <PackageGrid packages={GOODWE_PACKAGES_832} moduleType="8.32" testid="goodwe-grid-832" />

          <p className="text-xs text-gray-500 mt-6">
            *Prices assume standard installation and customer assignment of the applicable BSTC rebate to Supa Energy Hub. Additional site-specific costs may apply.
          </p>
        </div>
      </section>

      {/* STANDARD INSTALL + ADDITIONAL COSTS + SITE INSPECTION */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="bg-[#f8faf5] rounded-2xl border border-gray-100 p-6 sm:p-7">
            <h2 className="heading-2 text-[#3D5A47] mb-2 flex items-center gap-2"><CheckCircle className="w-5 h-5 text-[#B5D43B]" /> Standard installation</h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              Prices are based on standard installation conditions and customer assignment of the applicable BSTC rebate to
              Supa Energy Hub. Final price may vary depending on site conditions, switchboard requirements, battery location,
              cable run, wall surface, base/slab condition, safety requirements, and other installation factors.
            </p>
          </div>
          <div className="bg-[#fdf4ec] rounded-2xl border border-orange-100 p-6 sm:p-7">
            <h2 className="heading-2 text-[#3D5A47] mb-2 flex items-center gap-2"><Wrench className="w-5 h-5 text-orange-600" /> Additional costs that may apply</h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              <strong>Important Note:</strong> Additional costs may apply if required, including bollards at $200 per site,
              awning at $200 per site, additional cabling beyond 5 m at $25 per metre, concrete slab/base at $150, fireproof
              backing/cement sheet at $150, additional switchboard works, obstructed cabling pathway, or other site-specific
              labour and materials. These will be confirmed after the site inspection before installation is organised.
            </p>
          </div>
          <div className="bg-[#f8faf5] rounded-2xl border border-gray-100 p-6 sm:p-7">
            <h2 className="heading-2 text-[#3D5A47] mb-2 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-[#3D5A47]" /> Site inspection deposit</h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              Non-refundable deposit for a technician site inspection to confirm installation suitability and identify any
              additional costs, if required. If you proceed, this amount is deducted from your final balance payable. If you
              cancel after the inspection or after receiving any additional cost estimate, this amount is retained as the site
              inspection / cancellation fee.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#f8faf5] border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">FAQ</span>
          <h2 className="heading-2 text-[#3D5A47] mb-6">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="w-full" data-testid="goodwe-main-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-[#3D5A47] font-semibold text-sm hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-gray-600 text-sm">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#3D5A47] to-[#1a2820] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-3">Find your GoodWe battery package</h2>
          <p className="text-white/80 mb-6">Free quote. We handle the BSTC rebate paperwork and install everything end-to-end.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/get-estimate?source=goodwe-main">
              <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#1a2820] gap-2 font-bold px-6 py-5 rounded-xl" data-testid="goodwe-main-cta-quote">
                <ClipboardList className="w-4 h-4" /> Get My GoodWe Battery Quote
              </Button>
            </Link>
            <a href="tel:0478453548">
              <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 gap-2 px-6 py-5 rounded-xl">
                <Phone className="w-4 h-4" /> Call 0478 453 548
              </Button>
            </a>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export default GoodWePackagesPage;
